import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { css, withTheme, keyframes } from 'styled-components';
import { styled } from '../../utils/styled';
import { propStyles } from '../../utils/propStyles';
import Translate from '../Translate';
import Badge from '../Badge';
import Icon from '../Icon';
import { action, computed } from 'mobx';
import {
    DataStore,
    TranslationStore,
    AccessibilityStore,
    ConditionalDisclaimerStore,
    ScreenSizeStore,
    SuperscriptScope,
    QueryStore,
    EntityStore,
} from '../../stores';
import Checkbox from '../Checkbox';
import CopyDocumentUrlButton from './CopyDocumentUrlButton';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { deepMergeObjectsWithOptions } from '@kurtosys/ksys-app-template/dist/utils/object';
import InlineDisclaimers from '../InlineDisclaimers';
import {
    IFundCenterContext,
    IAnalytics,
} from '@kurtosys/ksys-app-template/dist/models/helpers/AnalyticsHelper';
import { languageFromCulture } from '../../utils/languageFromCulture';
import {
    isOptionLabelHidden,
    getOptionLabel,
    isDropdownEnabled,
} from '../../utils/document/downloadControls';
import {
    ActionOptionType,
    CheckboxOptions,
    CopyUrlActionOptions,
    CountOptions,
    DownloadActionOptions,
    DownloadControlActions,
    DropdownOptions,
    LinkActionOptions,
    PreviewActionOptions,
    TitleActionOptions,
} from '../../models/configuration/explorer/DownloadControls';
import DownloadControlsAdditionalElement from './DownloadControlsAdditonalElement';
import ExtendedInfo from '../ExtendedInfo';
import { IconAlignment } from '../../types';
import Dropdown from '../Dropdown';
import { DropdownItem } from '../../models/components/Dropdown/DropdownItem';
import { inject } from '../../utils';

const bounce = keyframes`
    from,
    20%,
    53%,
    80%,
    to {
      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
      transform: translate3d(0, 0, 0);
    }

    40%,
    43% {
      animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
      transform: translate3d(0, -0.5em, 0);
    }

    70% {
      animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
      transform: translate3d(0, -0.25em, 0);
    }

    90% {
      transform: translate3d(0, -0.05em, 0);
    }
`;

export const bounceRule = css`
    ${bounce}
`;

const DownloadControlsWrapper = styled<any, any>(
    'div',
    'downloadControlsWrapper',
    'downloadControls'
)`
    ${propStyles};
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

DownloadControlsWrapper.defaultProps = {
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: ['none', 'none none solid none'],
    hover: {
        bgColor: ['transparent', 'gray100'],
    },
};

const Wrapper = styled<any, any>('div', 'wrapper', 'downloadControls')`
    ${propStyles};
`;

Wrapper.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: '1 0 auto',
};

const IconWrapper = styled<any, any>('div', 'iconWrapper', 'downloadControls')`
    ${propStyles};
`;

IconWrapper.defaultProps = {
    alignItems: 'center',
};

const DownloadButtonIcon = styled<any, any>(
    Icon,
    'downloadButtonIcon',
    'downloadControls'
)``;

DownloadButtonIcon.defaultProps = {
    marginX: 'sm',
    height: '1.6em',
    width: '1.6em',
};

const PreviewButtonIcon = styled<any, any>(
    Icon,
    'previewButtonIcon',
    'downloadControls'
)``;

PreviewButtonIcon.defaultProps = {
    marginX: 'sm',
    height: '1.6em',
    width: '1.6em',
};

const DownloadButton = styled<any, any>(
    'button',
    'downloadButton',
    'downloadControls'
)`
    ${propStyles};
    &:hover {
        ${DownloadButtonIcon} {
            animation-name: ${bounceRule};
            animation-duration: 1s;
            animation-fill-mode: both;
            transform-origin: center bottom;
        }
    }
`;

DownloadButton.defaultProps = {
    alignItems: 'center',
    paddingX: '0',
    paddingY: '0',
    fontSize: 'sm',
    fontFamily: 'body',
    fontWeight: 'regular',
    textAlign: 'left',
    color: 'inherit',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    outline: 'none',
    hover: {
        color: 'primary',
    },
    justifyContent: 'space-between',
    flex: '1 1 auto',
    textDecoration: 'none',
};

const DownloadLink = styled<any, any>('a', 'downloadLink', 'downloadControls')`
    ${propStyles};
    &:hover {
        ${DownloadButtonIcon} {
            animation-name: ${bounceRule};
            animation-duration: 1s;
            animation-fill-mode: both;
            transform-origin: center bottom;
        }
    }
`;

// use same styles as the button
DownloadLink.defaultProps = DownloadButton.defaultProps;

const PreviewButton = styled<any, any>(
    'button',
    'previewButton',
    'downloadControls'
)`
    ${propStyles};
    &:hover {
        ${PreviewButtonIcon} {
            animation-name: ${bounceRule};
            animation-duration: 1s;
            animation-fill-mode: both;
            transform-origin: center bottom;
        }
    }
`;

PreviewButton.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    paddingX: '0',
    paddingY: '0',
    fontSize: 'inherit',
    fontFamily: 'body',
    fontWeight: 'regular',
    textAlign: 'left',
    color: 'inherit',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    outline: 'none',
    hover: {
        color: 'primary',
    },
    justifyContent: 'flex-end',
    flex: '0 0 auto',
};

const checkboxStyleProps = {
    CheckboxChecked: {
        height: '1.4em',
        width: '1.4em',
        marginX: 'xs',
    },
    CheckboxUnchecked: {
        height: '1.4em',
        width: '1.4em',
        marginX: 'xs',
    },
};

interface DownloadControlsProps {
    theme?: any;
    componentThemeKey?: 'string';
    dataStore?: DataStore;
    translationStore?: TranslationStore;
    entityStore?: EntityStore;
    label?: string;
    groupLabel?: string;
    showCount?: boolean;
    showDownload?: boolean;
    hidePreview?: boolean;
    count?: number;
    disabled?: boolean;
    downloadButtonFileName?: string;
    showLabel?: boolean;
    showGroupLabel?: boolean;
    fileName?: string;
    showCheckbox?: boolean;
    document?: any;
    documentDisplayName?: string;
    selected?: boolean;
    metricContext?: string;
    href?: string;
    documentType?: string;
    superscriptScope?: SuperscriptScope;
    accessibilityStore?: AccessibilityStore;
    clientCode?: string;
    screenSizeStore?: ScreenSizeStore;
    actions?: DownloadControlActions;
    queryStore?: QueryStore;
    onClick?(event: React.MouseEvent<MouseEvent>): void;
    onPreviewClick?(
        event: React.MouseEvent,
        elementOptions: PreviewActionOptions
    ): void;
    /**
     * @title On Change
     *
     * @param document the document entity
     * @param key the key used to group the selected document by the entity
     */
    onChange?(document: any, key: string): void;
}

@inject(
    'accessibilityStore',
    'dataStore',
    'translationStore',
    'screenSizeStore',
    'queryStore',
    'entityStore'
)
@observer
class DownloadControls extends Component<DownloadControlsProps> {
    constructor(props: DownloadControlsProps) {
        super(props);
        this.handleOnChange = this.handleOnChange.bind(this);
        this.handleOnClick = this.handleOnClick.bind(this);
    }

    @computed
    get theme() {
        const { theme, componentThemeKey = '' } = this.props;
        const componentKeys = [
            'DownloadControls',
            'DownloadControlsMobile',
            'DownloadButton',
            'DownloadButtonMobile',
            'DownloadButtonIconWrapper',
            'DownloadButtonIconWrapMob',
            'DownloadButtonIcon',
            'DownloadButtonIconMobile',
            'DownloadButtonIconNoLabel',
            'PreviewButtonIcon',
            'PreviewButtonIconNoLabel',
            'PreviewButtonIconMobile',
            'DownloadCount',
            'DownloadCountMobile',
            'CheckboxLabel',
            'CheckboxChecked',
            'CheckboxUnchecked',
            'DownloadCopyUrlButton',
            'DownloadCopyUrlIcon',
            'DownloadCopyUrlSuccessIcon',
            'DownloadAddElement',
            'DownloadAddElementLink',
            'DownloadAddElementLabel',
            'DownloadAddElementIcon',
            'DownloadAddElementIconDis',
            'DownloadDropdownWrapper',
            'DownloadDropdownButton',
            'DownloadDropdownButtonIcon',
            'DownloadDropdownMenu',
            'DownloadDropdownListItem',
        ];
        const variantTheme = { ...theme };

        for (const key of componentKeys) {
            const componentTheme = theme[`${componentThemeKey}${key}`];
            if (componentTheme) {
                variantTheme[key] = componentTheme;
            }
        }

        return variantTheme;
    }

    @action
    handleOnChange(document) {
        const { onChange, entityStore } = this.props;
        if (onChange) {
            onChange(document, entityStore?.value?.clientCode);
        }
    }

    @computed
    get title() {
        const {
            showLabel,
            label,
            showGroupLabel,
            groupLabel,
            translationStore,
            documentDisplayName,
        } = this.props;
        let text = '';
        if (showGroupLabel && groupLabel) {
            text += ` ${groupLabel}`;
        }
        if (showLabel && label) {
            text = ` ${label}`;
        }
        if (documentDisplayName) {
            text = `${documentDisplayName}`;
        }
        return translationStore.translate(text);
    }

    @computed
    get checkboxAriaLabel() {
        const { selected, translationStore } = this.props;
        const text = selected ? `Deselect {title}` : `Select {title}`;
        return translationStore.translate(text.trim(), { title: this.title });
    }

    @computed
    get showLabel() {
        const { showLabel = true } = this.props;
        return this.showDropdown ? true : showLabel;
    }

    @computed
    get showGroupLabel() {
        const { showGroupLabel = false } = this.props;
        return showGroupLabel;
    }

    // Set up the object assign to avoid breaking existing style selectors
    @computed
    get downloadControlsTheme() {
        const { screenSizeStore } = this.props;
        const { isMobile } = screenSizeStore;
        const { theme } = this;
        return isMobile
            ? { ...theme.DownloadControls, ...theme.DownloadControlsMobile }
            : theme.DownloadControls;
    }

    @computed
    get downloadButtonTheme() {
        const { screenSizeStore } = this.props;
        const { isMobile } = screenSizeStore;
        const { theme } = this;
        return isMobile
            ? { ...theme.DownloadButton, ...theme.DownloadButtonMobile }
            : theme.DownloadButton;
    }

    @computed
    get downloadButtonIconWrapperTheme() {
        const { screenSizeStore } = this.props;
        const { isMobile } = screenSizeStore;
        const { theme } = this;
        return isMobile
            ? {
                  ...theme.DownloadButtonIconWrapper,
                  ...theme.DownloadButtonIconWrapMob,
              }
            : theme.DownloadButtonIconWrapper;
    }

    // default icon to center when no label and right for mobile
    @computed
    get downloadButtonIconCenterTheme() {
        const { theme } = this;
        let buttonIconTheme = theme.DownloadButtonIcon;
        if (!this.showLabel) {
            buttonIconTheme = {
                ...theme.DownloadButtonIcon,
                ...theme.DownloadButtonIconNoLabel,
            };
        }
        return buttonIconTheme;
    }

    @computed
    get downloadButtonIconTheme() {
        const { screenSizeStore } = this.props;
        const { isMobile } = screenSizeStore;
        const { theme } = this;
        return isMobile
            ? {
                  ...this.downloadButtonIconCenterTheme,
                  ...theme.DownloadButtonIconMobile,
              }
            : this.downloadButtonIconCenterTheme;
    }

    // default icon to center when no label and right for mobile
    @computed
    get previewButtonIconCenterTheme() {
        const { theme } = this;
        let buttonIconTheme = theme.PreviewButtonIcon;
        if (!this.showLabel) {
            buttonIconTheme = {
                ...theme.PreviewButtonIcon,
                ...theme.PreviewButtonIconNoLabel,
            };
        }
        return buttonIconTheme;
    }

    @computed
    get previewButtonIconTheme() {
        const { screenSizeStore } = this.props;
        const { isMobile } = screenSizeStore;
        const { theme } = this;
        return isMobile
            ? {
                  ...this.previewButtonIconCenterTheme,
                  ...theme.PreviewButtonIconMobile,
              }
            : this.previewButtonIconCenterTheme;
    }

    @computed
    get downloadCountTheme() {
        const { screenSizeStore } = this.props;
        const { isMobile } = screenSizeStore;
        const { theme } = this;
        return isMobile
            ? { ...theme.DownloadCount, ...theme.DownloadCountMobile }
            : theme.DownloadCount;
    }

    @computed
    get checkboxTheme() {
        const { theme } = this.props;
        return {
            ...theme,
            ...checkboxStyleProps,
            ...(theme.CheckboxLabel && { CheckboxLabel: theme.CheckboxLabel }),
            ...(theme.CheckboxChecked && {
                CheckboxChecked: theme.CheckboxChecked,
            }),
            ...(theme.CheckboxUnchecked && {
                CheckboxUnchecked: theme.CheckboxUnchecked,
            }),
        };
    }

    @computed
    get dropdownItemTheme() {
        if (this.showDropdown) {
            return {
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                textDecoration: 'none',
                fontSize: 'inherit',
                color: 'body',
                injectStyles: 'column-gap: 1em;',
                hover: {
                    color: 'inherit',
                },
            };
        }

        return {};
    }

    @computed
    get dropdownItemIconTheme() {
        if (this.showDropdown) {
            return {
                height: '1.4em',
                width: '1.4em',
                marginX: '0',
            };
        }

        return {};
    }

    @computed
    get dropdownItemIconDisabledTheme() {
        if (this.showDropdown) {
            return {
                height: '1.4em',
                width: '1.4em',
                marginX: '0',
                cursor: 'default',
            };
        }

        return {
            cursor: 'default',
        };
    }

    @computed
    get dropdownTheme() {
        return {
            ...this.theme,
            DownloadDropdownWrapper: {
                color: 'body',
                bgColor: 'transparent',
                padding: '0',
                marginX: 'sm',
                flex: 'none',
                fontSize: 'sm',
                ...this.theme.DownloadDropdownWrapper,
            },
            DownloadDropdownButton: {
                fontSize: 'xs',
                margin: '0',
                padding: 'xs',
                ...this.theme.DownloadDropdownButton,
            },
            DownloadDropdownButtonIcon: {
                height: '1.4em',
                width: '1.4em',
                ...this.theme.DownloadDropdownButtonIcon,
            },
            DownloadDropdownMenu: {
                fontSize: 'xs',
                margin: '0',
                minWidth: 'fit-content',
                width: 'auto',
                whiteSpace: 'nowrap',
                right: '0',
                ...this.theme.DownloadDropdownMenu,
            },
            DownloadDropdownListItem: {
                bgColor: 'white',
                hover: {
                    bgColor: 'rgba(0,0,0,0.025) !important',
                },
                alternated: {
                    bgColor: 'white',
                },
                ...this.theme.DownloadDropdownListItem,
            },
        };
    }

    @computed
    get showDownload(): boolean {
        if (this.props.showDownload !== undefined) {
            return this.props.showDownload;
        }

        return true;
    }

    @computed
    get documentCount(): number {
        if (this.props.count !== undefined) {
            return this.props.count;
        }
        return 0;
    }

    @computed
    get showPreview(): boolean {
        const isSingleDocument = this.documentCount === 1;
        const hidePreviewForInstance = this.props.hidePreview;
        return isSingleDocument && !hidePreviewForInstance;
    }

    @computed
    get defaultPreviewIconFilename(): string {
        const { dataStore } = this.props;

        return dataStore?.config?.documentPreviewIcon || 'PreviewIcon';
    }

    @computed
    get actions(): DownloadControlActions | undefined {
        return this.props.actions;
    }

    @computed
    get displayDisabledIcons(): boolean {
        return this.props.actions?.displayDisabledIcons;
    }

    @computed
    get countOptions(): CountOptions {
        return this.actions?.countOptions || {};
    }

    get zeroDocumentCountOverride(): string | undefined {
        return (
            this.countOptions?.zeroCountOverride ||
            this.props.dataStore?.config?.zeroDocumentCountOverride
        );
    }

    @computed
    get hideCount(): boolean {
        if (this.countOptions?.hide !== undefined) {
            return this.countOptions.hide;
        }
        if (
            this.props.dataStore?.config?.globalDownloadControlActions
                ?.countOptions?.hide !== undefined
        ) {
            return this.props.dataStore?.config?.globalDownloadControlActions
                ?.countOptions?.hide;
        }
    }

    @computed
    get hideZeroCount(): boolean {
        if (this.countOptions?.hideZeroCount !== undefined) {
            return this.countOptions?.hideZeroCount;
        }

        if (this.props.dataStore?.config?.hideZeroDocumentCount !== undefined) {
            return this.props.dataStore?.config?.hideZeroDocumentCount;
        }

        return false;
    }

    @computed
    get showCountDefault(): boolean {
        if (this.hideZeroCount && this.documentCount === 0) {
            return false;
        }
        if (this.hideCount) {
            return false;
        }
        return true;
    }

    @computed
    get checkboxOptions(): CheckboxOptions | undefined {
        return this.actions?.checkboxOptions;
    }

    @computed
    get checkedIconFileName(): string {
        // if specified in config, its an indication that it should show
        if (this.checkboxOptions?.iconOptions?.checkedFilename) {
            return this.checkboxOptions.iconOptions.checkedFilename;
        }
        return 'CheckboxMarkedIcon';
    }

    @computed
    get uncheckedIconFileName(): string {
        // if specified in config, its an indication that it should show
        if (this.checkboxOptions?.iconOptions?.uncheckedFilename) {
            return this.checkboxOptions.iconOptions.uncheckedFilename;
        }
        return 'CheckboxIcon';
    }

    @computed
    get showCheckbox(): boolean {
        if (this.checkboxOptions?.hide === true) {
            return false;
        }
        return this.props.showCheckbox || false;
    }

    @computed
    get showCheckboxLabel(): boolean {
        return !this.checkboxOptions?.labelOptions?.hide;
    }

    @computed
    get checkboxLabel(): string {
        const {
            document,
            entityStore,
            queryStore,
            screenSizeStore,
        } = this.props;
        return getOptionLabel({
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            labelKey: 'label',
            downloadControlActions: this.actions,
            labelOptions: this.checkboxOptions?.labelOptions,
        });
    }

    @computed
    get dropdownOptions(): DropdownOptions | undefined {
        return this.actions?.dropdownOptions;
    }

    @computed
    get hideDropdownLabel(): boolean {
        const { actions, screenSizeStore } = this.props;
        const optionLabelIsHidden = isOptionLabelHidden({
            screenSizeStore,
            downloadControlActions: actions,
            labelOptions: this.dropdownOptions?.labelOptions,
        });
        return optionLabelIsHidden;
    }

    @computed
    get dropdownLabel(): string {
        const { document, entityStore, queryStore } = this.props;
        const defaultLabel = 'Download Controls';
        if (this.dropdownOptions?.labelOptions?.label) {
            const labelQuery = this.dropdownOptions?.labelOptions?.label;
            return queryStore.queryOrDefault(
                labelQuery,
                {
                    entityByType: {
                        [labelQuery?.entityType]: {
                            ...entityStore?.value,
                        },
                    },
                    document,
                },
                defaultLabel
            );
        }
        return defaultLabel;
    }

    @computed
    get dropdownCollapsedIconFilename(): string {
        return (
            this.dropdownOptions.iconOptions?.collapsedFilename ||
            'DropdownChevronIcon'
        );
    }

    @computed
    get dropdownExpandedIconFilename(): string {
        return (
            this.dropdownOptions.iconOptions?.expandedFilename ||
            'DropdownChevronIconExpanded'
        );
    }

    @computed
    get showDropdown(): boolean {
        const { screenSizeStore } = this.props;
        return isDropdownEnabled(screenSizeStore, this.actions);
    }

    @computed
    get actionElementComponents(): Array<DropdownItem> {
        return (
            this.actions?.elements?.reduce((components, elementOptions) => {
                let id;
                let component;
                switch (elementOptions.type) {
                    case ActionOptionType.copyUrl:
                        id = 'copyUrl';
                        component = this.getCopyUrlElement(elementOptions);
                        break;
                    case ActionOptionType.download:
                        id = 'download';
                        component = this.getDownloadElement(elementOptions);
                        break;
                    case ActionOptionType.link:
                        id = 'link';
                        component = this.getLinkElement(elementOptions);
                        break;
                    case ActionOptionType.preview:
                        id = 'preview';
                        component = this.getPreviewElement(elementOptions);
                        break;
                    case ActionOptionType.title:
                        id = 'title';
                        component = this.getTitleElement(elementOptions);
                        break;
                    default:
                        break;
                }
                if (component) {
                    components.push({ id, component, label: id });
                }
                return components;
            }, []) || []
        );
    }

    getDownloadComponentProps(
        elementOptions: DownloadActionOptions | TitleActionOptions
    ) {
        const {
            actions,
            disabled,
            document,
            entityStore,
            fileName,
            href,
            queryStore,
            screenSizeStore,
            translationStore,
            dataStore,
        } = this.props;

        const { getDocumentLink } = dataStore;

        const defaultLabel = `{title} Download`;

        const ariaPlaceholderLabel = getOptionLabel({
            defaultLabel,
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            skipHiddenCheck: true,
            labelKey: 'label',
            downloadControlActions: actions,
            labelOptions: elementOptions.labelOptions,
        });

        const ariaLabel =
            ariaPlaceholderLabel &&
            translationStore.translate(ariaPlaceholderLabel.trim(), {
                title: this.title,
            });

        const componentProps: any = {
            disabled,
            title: ariaLabel,
            'aria-label': ariaLabel,
            // Used for Adobe analytics tracking
            'data-metrics-link-type': 'download',
            'data-metrics-link-dest': fileName,
            'data-metric-id': this.metricId,
            ...{
                ...this.downloadButtonTheme,
                ...this.dropdownItemTheme,
            },
        };
        componentProps.onClick = e => this.handleOnClick(e);
        if (this.useDownloadLink) {
            if ((elementOptions as DownloadActionOptions)?.url) {
                componentProps.href = getDocumentLink(
                    [document],
                    entityStore?.value,
                    (elementOptions as DownloadActionOptions)?.url
                );
            } else {
                componentProps.href = href;
            }
        }
        return componentProps;
    }

    getCopyUrlElement(elementOptions: CopyUrlActionOptions) {
        const { document, fileName, metricContext } = this.props;

        return (
            (this.displayDisabledIcons || this.documentCount > 0) && (
                <CopyDocumentUrlButton
                    actions={this.actions}
                    fileName={fileName}
                    metricContext={metricContext}
                    document={document}
                    documentDisplayName={this.title}
                    theme={{
                        ...this.theme,
                        DownloadCopyUrlButton: this.dropdownItemTheme,
                        DownloadCopyUrlSuccessIcon: this.dropdownItemIconTheme,
                        DownloadCopyUrlIcon: this.dropdownItemIconTheme,
                    }}
                    options={elementOptions}
                />
            )
        );
    }

    @computed
    get isDownloadActive(): boolean {
        return this.showDownload && this.documentCount !== 0;
    }

    getDownloadElement(elementOptions: DownloadActionOptions) {
        const {
            accessibilityStore,
            actions,
            dataStore,
            document,
            documentType,
            entityStore,
            label,
            superscriptScope,
            theme,
            translationStore,
            queryStore,
            screenSizeStore,
        } = this.props;
        const DownloadComponent = this.DownloadComponent;

        const documentCulture = document && document.cultureCode;
        let conditionalDisclaimers;
        if (dataStore && dataStore.disclaimerStore) {
            conditionalDisclaimers = ConditionalDisclaimerStore.getConditionalsForDocumentTypeAndCulture(
                documentType,
                documentCulture
            );
        }

        const superscriptThemeDefault = deepMergeObjectsWithOptions(
            { arrayMergeStrategy: 'DeepMerge' },
            {
                Superscript: {
                    padding: '0 0 10px 0',
                },
            },
            theme
        );

        const optionLabelIsHidden = isOptionLabelHidden({
            screenSizeStore,
            downloadControlActions: actions,
            labelOptions: elementOptions.labelOptions,
        });

        let elementLabel;
        if (!optionLabelIsHidden) {
            if (elementOptions.labelOptions?.label) {
                const placeholderLabel = getOptionLabel({
                    document,
                    entityStore,
                    queryStore,
                    screenSizeStore,
                    labelKey: 'label',
                    downloadControlActions: actions,
                    labelOptions: elementOptions.labelOptions,
                });

                elementLabel =
                    placeholderLabel &&
                    translationStore.translate(placeholderLabel.trim(), {
                        title: this.title,
                    });
            } else if (this.showLabel && label) {
                elementLabel = translationStore.translate(label);
            }
        }

        return (
            (this.displayDisabledIcons || this.isDownloadActive) && (
                <DownloadComponent
                    display="flex"
                    textAlign="right"
                    {...this.getDownloadComponentProps(elementOptions)}
                >
                    {elementLabel && (
                        <InlineDisclaimers
                            conditionalDisclaimers={conditionalDisclaimers}
                            superscriptScope={superscriptScope}
                            theme={superscriptThemeDefault}
                            analyticsContext={{
                                source: 'downloadControl',
                                sourceContext: elementLabel,
                            }}
                        >
                            {elementLabel}
                        </InlineDisclaimers>
                    )}
                    {!elementOptions?.iconOptions?.hide && (
                        <IconWrapper {...this.downloadButtonIconWrapperTheme}>
                            <DownloadButtonIcon
                                aria-label={accessibilityStore.buttonAccessibilityValue(
                                    'DownloadButtonIcon'
                                )}
                                fileName={
                                    !this.isDownloadActive
                                        ? elementOptions.iconOptions
                                              ?.disabledFilename
                                        : elementOptions.iconOptions
                                              ?.filename || 'DownloadIcon'
                                }
                                {...this.downloadButtonIconTheme}
                                {...this.dropdownItemIconTheme}
                            />
                        </IconWrapper>
                    )}
                </DownloadComponent>
            )
        );
    }

    getLinkElement(elementOptions: LinkActionOptions) {
        return (
            <DownloadControlsAdditionalElement
                actions={this.actions}
                additionalElement={elementOptions}
                document={this.props.document}
                theme={{
                    ...this.theme,
                    ...{
                        DownloadAddElement: this.dropdownItemTheme,
                        DownloadAddElementLink: this.dropdownItemTheme,
                        DownloadAddElementIcon: this.dropdownItemIconTheme,
                        DownloadAddElementIconDis: this
                            .dropdownItemIconDisabledTheme,
                    },
                }}
            />
        );
    }

    @computed
    get isPreviewActive(): boolean {
        return this.showPreview && this.documentCount === 1;
    }

    getPreviewElement(elementOptions: PreviewActionOptions) {
        const {
            accessibilityStore,
            actions,
            disabled,
            document,
            entityStore,
            fileName,
            onPreviewClick,
            metricContext,
            queryStore,
            screenSizeStore,
            translationStore,
        } = this.props;

        const defaultLabel = `{title} Preview`;

        const placeholderLabel = getOptionLabel({
            defaultLabel,
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            labelKey: 'label',
            downloadControlActions: actions,
            labelOptions: elementOptions.labelOptions,
        });

        const previewLabel =
            placeholderLabel &&
            translationStore.translate(placeholderLabel.trim(), {
                title: this.title,
            });

        const ariaPlaceholderLabel = getOptionLabel({
            defaultLabel,
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            skipHiddenCheck: true,
            labelKey: 'label',
            downloadControlActions: actions,
            labelOptions: elementOptions.labelOptions,
        });

        const previewAriaLabel =
            ariaPlaceholderLabel &&
            translationStore.translate(ariaPlaceholderLabel.trim(), {
                title: this.title,
            });

        return (
            (this.displayDisabledIcons || this.isPreviewActive) && (
                <PreviewButton
                    title={previewAriaLabel}
                    aria-label={previewAriaLabel}
                    onClick={event => onPreviewClick(event, elementOptions)}
                    disabled={disabled}
                    // Used for Adobe analytics tracking
                    data-metrics-link-type={'preview'}
                    data-metrics-link-dest={fileName}
                    data-metric-id={`downloadControl-preview-${metricContext}`}
                    {...{
                        ...this.downloadButtonTheme,
                        ...this.dropdownItemTheme,
                    }}
                >
                    {previewLabel}
                    {!elementOptions?.iconOptions?.hide && (
                        <IconWrapper {...this.downloadButtonIconWrapperTheme}>
                            <PreviewButtonIcon
                                aria-label={accessibilityStore.buttonAccessibilityValue(
                                    'PreviewButtonIcon'
                                )}
                                fileName={
                                    !this.isPreviewActive
                                        ? elementOptions.iconOptions
                                              .disabledFilename
                                        : elementOptions.iconOptions
                                              ?.filename ||
                                          this.defaultPreviewIconFilename
                                }
                                {...this.dropdownItemIconTheme}
                            />
                        </IconWrapper>
                    )}
                </PreviewButton>
            )
        );
    }

    getTitleElement(elementOptions: TitleActionOptions) {
        const {
            dataStore,
            document,
            documentType,
            entityStore,
            groupLabel,
            label,
            superscriptScope,
            theme,
            translationStore,
            queryStore,
            screenSizeStore,
        } = this.props;
        const DownloadComponent = this.DownloadComponent;

        const documentCulture = document && document.cultureCode;
        let conditionalDisclaimers;
        if (dataStore && dataStore.disclaimerStore) {
            conditionalDisclaimers = ConditionalDisclaimerStore.getConditionalsForDocumentTypeAndCulture(
                documentType,
                documentCulture
            );
        }

        const superscriptThemeDefault = deepMergeObjectsWithOptions(
            { arrayMergeStrategy: 'DeepMerge' },
            {
                Superscript: {
                    padding: '0 0 10px 0',
                },
            },
            theme
        );

        let elementLabel;
        if (!elementOptions.labelOptions?.hide) {
            if (elementOptions.labelOptions?.label) {
                elementLabel = getOptionLabel({
                    document,
                    entityStore,
                    queryStore,
                    screenSizeStore,
                    labelKey: 'label',
                    downloadControlActions: this.actions,
                    labelOptions: elementOptions.labelOptions,
                });
            } else if (this.showLabel && label) {
                elementLabel = translationStore.translate(label);
            }
        }

        let elementGroupLabel;
        if (this.showGroupLabel && groupLabel) {
            elementGroupLabel = translationStore.translate(groupLabel);
        }

        return (
            (elementGroupLabel || elementLabel) &&
            this.documentCount !== 0 && (
                <DownloadComponent
                    display="flex"
                    textAlign="right"
                    {...this.getDownloadComponentProps(elementOptions)}
                >
                    <InlineDisclaimers
                        conditionalDisclaimers={conditionalDisclaimers}
                        superscriptScope={superscriptScope}
                        theme={superscriptThemeDefault}
                        analyticsContext={{
                            source: 'downloadControl',
                            sourceContext: elementLabel,
                        }}
                    >
                        {groupLabel && this.showGroupLabel && (
                            <Translate>{groupLabel}</Translate>
                        )}
                        {elementLabel}
                    </InlineDisclaimers>
                </DownloadComponent>
            )
        );
    }

    @computed
    get metricId() {
        const { metricContext, entityStore, document } = this.props;
        const entityId = entityStore?.value?.entityId;
        const documentClientCode = document && document.clientCode;
        let identifierSuffix = '';
        if (entityId) {
            identifierSuffix += `-${entityId}`;
        }
        if (documentClientCode) {
            identifierSuffix += `-${documentClientCode}`;
        }
        return `downloadControl-${metricContext}${identifierSuffix}`;
    }

    @computed
    get useDownloadLink(): boolean {
        const { href } = this.props;
        return !isNullOrEmpty(href);
    }

    // Use download link when 1 document so that the GET http method can be used. ZIP currently doesn't support multiple document codes
    @computed
    get DownloadComponent(): React.ComponentType {
        if (this.useDownloadLink) {
            return DownloadLink;
        }
        return DownloadButton;
    }

    handleOnClick(event: React.MouseEvent<MouseEvent>) {
        event.stopPropagation();
        const { onClick } = this.props;
        this.logDataLayer();
        if (!this.useDownloadLink) {
            // If not using the link fire off the passed onClick function
            onClick(event);
        }
    }

    logDataLayer() {
        const {
            dataStore,
            count = 0,
            fileName,
            entityStore,
            clientCode,
            document,
            documentType = 'csv',
        } = this.props;
        const linkedEntity = entityStore?.value?.clientCode || clientCode;
        // Still log to the dataLayer if using the link
        const analyticsContext: IAnalytics<IFundCenterContext> = {
            event: 'document_download',
            eventType: 'click',
            context: {
                linkedEntity,
                filename: fileName,
            },
        };
        if (count > 1) {
            // Multiple documents will just need the amount of documents
            // tslint:disable-next-line: prefer-object-spread
            analyticsContext.context = Object.assign(analyticsContext.context, {
                documentCount: count,
            });
        } else if (count === 1 || document) {
            // Single documents get the language and document type
            const cultureCode =
                (document && document.cultureCode) || dataStore.culture;
            const docLanguage = languageFromCulture(cultureCode);
            // tslint:disable-next-line: prefer-object-spread
            analyticsContext.context = Object.assign(analyticsContext.context, {
                language: docLanguage,
                type: documentType,
            });
        } else {
            console.warn(
                `Could not determine the context, default context applied: ${analyticsContext.context}`
            );
        }
        dataStore.analyticsHelper.logEvent(analyticsContext);
    }

    @computed
    get disabledExtendedInfo(): boolean {
        if (this.actions?.disableExtendedInfo !== undefined) {
            return this.actions.disableExtendedInfo;
        }
        return false;
    }

    @computed
    get extendedInfoPosition(): IconAlignment {
        const { dataStore } = this.props;
        const config = dataStore.config.extendedInfo;
        const position = config?.iconPosition;
        return position || 'left';
    }

    render() {
        const {
            dataStore,
            translationStore,
            entityStore,
            label,
            groupLabel,
            showCount = this.showCountDefault,
            showDownload = true,
            count = 0,
            disabled,
            downloadButtonFileName = 'DownloadIcon',
            fileName,
            selected,
            documentType,
            onClick,
            onPreviewClick,
            onChange,
            document,
            metricContext,
            accessibilityStore,
            href,
            ...props
        } = this.props;
        const {
            downloadControlsTheme,
            downloadCountTheme,
            checkboxTheme,
            theme,
        } = this;

        const extendedInfoComponent = !this.disabledExtendedInfo && (
            <ExtendedInfo
                shareClass={entityStore?.value}
                document={document}
                themeOverride={{
                    ExtendedInfoIconWrapper: theme.DocumentInfoIconWrapper,
                    ExtendedInfoWrapper: theme.DocumentInfoWrapper,
                }}
            />
        );

        return (
            <Fragment>
                {this.showCheckbox && (
                    <Checkbox
                        label={this.checkboxLabel}
                        showLabel={this.showCheckboxLabel}
                        ariaLabel={this.checkboxAriaLabel}
                        checked={selected}
                        onChange={onChange}
                        theme={{
                            ...theme,
                            ...checkboxTheme,
                            ...{
                                checkBoxLabel: {
                                    opacity: disabled ? 0.5 : 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    color: 'body',
                                },
                            },
                        }}
                        metricContext={this.metricId}
                        checkedIconFileName={this.checkedIconFileName}
                        uncheckedIconFileName={this.uncheckedIconFileName}
                    />
                )}

                <Wrapper {...props} {...downloadControlsTheme}>
                    {this.extendedInfoPosition === 'left' &&
                        count === 1 &&
                        extendedInfoComponent}

                    {this.showDropdown ? (
                        <Dropdown
                            items={this.actionElementComponents}
                            selectItemFn={() => undefined}
                            itemLabelFn={item => item.component}
                            placeHolder={this.dropdownLabel}
                            chevronIcon={this.dropdownCollapsedIconFilename}
                            chevronIconExpanded={
                                this.dropdownExpandedIconFilename
                            }
                            metricId={this.metricId}
                            accessibilityStoreKey="DownloadControlsDropdown"
                            hideLabel={this.hideDropdownLabel}
                            themeKeys={{
                                root: 'DownloadDropdownWrapper',
                                button: 'DownloadDropdownButton',
                                buttonIcon: 'DownloadDropdownButtonIcon',
                                listWrapper: 'DownloadDropdownMenu',
                                list: 'DownloadDropdownList',
                                listItem: 'DownloadDropdownListItem',
                                disclaimerWrapper:
                                    'DownloadDropdownDisclaimerWrapper',
                            }}
                            theme={this.dropdownTheme}
                        />
                    ) : (
                        this.actionElementComponents.map(
                            ({ id, component }, index) => (
                                <Fragment key={`${id}-${index}`}>
                                    {component}
                                </Fragment>
                            )
                        )
                    )}
                    {showCount && (
                        <Badge bgColor="primary" {...downloadCountTheme}>
                            {count === 0 && this.zeroDocumentCountOverride
                                ? translationStore.translate(
                                      this.zeroDocumentCountOverride
                                  )
                                : count}
                        </Badge>
                    )}

                    {this.extendedInfoPosition === 'right' &&
                        count === 1 &&
                        extendedInfoComponent}
                </Wrapper>
            </Fragment>
        );
    }
}

export default withTheme(DownloadControls) as any;



// WEBPACK FOOTER //
// ./src/Components/Document/DownloadControls.tsx